import _classCallCheck from "/var/www/mol_finder/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/var/www/mol_finder/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "/var/www/mol_finder/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/possibleConstructorReturn";
import _get from "/var/www/mol_finder/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/get";
import _getPrototypeOf from "/var/www/mol_finder/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "/var/www/mol_finder/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/inherits";

/**
 * @license
 * Copyright 2018 Google LLC
 *
 * Use of this source code is governed by an MIT-style
 * license that can be found in the LICENSE file or at
 * https://opensource.org/licenses/MIT.
 * =============================================================================
 */

/**
 * TensorFlow.js Layers: Pooling Layers.
 */
import * as tfc from '@tensorflow/tfjs-core';
import { serialization, tidy } from '@tensorflow/tfjs-core';
import { imageDataFormat } from '../backend/common';
import * as K from '../backend/tfjs_backend';
import { checkDataFormat, checkPaddingMode, checkPoolMode } from '../common';
import { InputSpec } from '../engine/topology';
import { Layer } from '../engine/topology';
import { NotImplementedError, ValueError } from '../errors';
import { convOutputLength } from '../utils/conv_utils';
import { assertPositiveInteger } from '../utils/generic_utils';
import { getExactlyOneShape, getExactlyOneTensor } from '../utils/types_utils';
import { preprocessConv2DInput, preprocessConv3DInput } from './convolutional';
/**
 * 2D pooling.
 * @param x
 * @param poolSize
 * @param stridesdes strides. Defaults to [1, 1].
 * @param padding padding. Defaults to 'valid'.
 * @param dataFormat data format. Defaults to 'channelsLast'.
 * @param poolMode Mode of pooling. Defaults to 'max'.
 * @returns Result of the 2D pooling.
 */

export function pool2d(x, poolSize, strides, padding, dataFormat, poolMode) {
  return tidy(function () {
    checkDataFormat(dataFormat);
    checkPoolMode(poolMode);
    checkPaddingMode(padding);

    if (strides == null) {
      strides = [1, 1];
    }

    if (padding == null) {
      padding = 'valid';
    }

    if (dataFormat == null) {
      dataFormat = imageDataFormat();
    }

    if (poolMode == null) {
      poolMode = 'max';
    } // TODO(cais): Remove the preprocessing step once deeplearn.js supports
    // dataFormat as an input argument.


    x = preprocessConv2DInput(x, dataFormat); // x is NHWC after preprocessing.

    var y;
    var paddingString = padding === 'same' ? 'same' : 'valid';

    if (poolMode === 'max') {
      // TODO(cais): Rank check?
      y = tfc.maxPool(x, poolSize, strides, paddingString);
    } else {
      // 'avg'
      // TODO(cais): Check the dtype and rank of x and give clear error message
      //   if those are incorrect.
      y = tfc.avgPool( // TODO(cais): Rank check?
      x, poolSize, strides, paddingString);
    }

    if (dataFormat === 'channelsFirst') {
      y = tfc.transpose(y, [0, 3, 1, 2]); // NHWC -> NCHW.
    }

    return y;
  });
}
/**
 * 3D pooling.
 * @param x
 * @param poolSize. Default to [1, 1, 1].
 * @param strides strides. Defaults to [1, 1, 1].
 * @param padding padding. Defaults to 'valid'.
 * @param dataFormat data format. Defaults to 'channelsLast'.
 * @param poolMode Mode of pooling. Defaults to 'max'.
 * @returns Result of the 3D pooling.
 */

export function pool3d(x, poolSize, strides, padding, dataFormat, poolMode) {
  return tidy(function () {
    checkDataFormat(dataFormat);
    checkPoolMode(poolMode);
    checkPaddingMode(padding);

    if (strides == null) {
      strides = [1, 1, 1];
    }

    if (padding == null) {
      padding = 'valid';
    }

    if (dataFormat == null) {
      dataFormat = imageDataFormat();
    }

    if (poolMode == null) {
      poolMode = 'max';
    } // x is NDHWC after preprocessing.


    x = preprocessConv3DInput(x, dataFormat);
    var y;
    var paddingString = padding === 'same' ? 'same' : 'valid';

    if (poolMode === 'max') {
      y = tfc.maxPool3d(x, poolSize, strides, paddingString);
    } else {
      // 'avg'
      y = tfc.avgPool3d(x, poolSize, strides, paddingString);
    }

    if (dataFormat === 'channelsFirst') {
      y = tfc.transpose(y, [0, 4, 1, 2, 3]); // NDHWC -> NCDHW.
    }

    return y;
  });
}
/**
 * Abstract class for different pooling 1D layers.
 */

export var Pooling1D = /*#__PURE__*/function (_Layer) {
  _inherits(Pooling1D, _Layer);

  /**
   *
   * @param args Parameters for the Pooling layer.
   *
   * config.poolSize defaults to 2.
   */
  function Pooling1D(args) {
    var _this;

    _classCallCheck(this, Pooling1D);

    if (args.poolSize == null) {
      args.poolSize = 2;
    }

    _this = _possibleConstructorReturn(this, _getPrototypeOf(Pooling1D).call(this, args));

    if (typeof args.poolSize === 'number') {
      _this.poolSize = [args.poolSize];
    } else if (Array.isArray(args.poolSize) && args.poolSize.length === 1 && typeof args.poolSize[0] === 'number') {
      _this.poolSize = args.poolSize;
    } else {
      throw new ValueError("poolSize for 1D convolutional layer must be a number or an " + "Array of a single number, but received " + "".concat(JSON.stringify(args.poolSize)));
    }

    assertPositiveInteger(_this.poolSize, 'poolSize');

    if (args.strides == null) {
      _this.strides = _this.poolSize;
    } else {
      if (typeof args.strides === 'number') {
        _this.strides = [args.strides];
      } else if (Array.isArray(args.strides) && args.strides.length === 1 && typeof args.strides[0] === 'number') {
        _this.strides = args.strides;
      } else {
        throw new ValueError("strides for 1D convolutional layer must be a number or an " + "Array of a single number, but received " + "".concat(JSON.stringify(args.strides)));
      }
    }

    assertPositiveInteger(_this.strides, 'strides');
    _this.padding = args.padding == null ? 'valid' : args.padding;
    checkPaddingMode(_this.padding);
    _this.inputSpec = [new InputSpec({
      ndim: 3
    })];
    return _this;
  }

  _createClass(Pooling1D, [{
    key: "computeOutputShape",
    value: function computeOutputShape(inputShape) {
      inputShape = getExactlyOneShape(inputShape);
      var length = convOutputLength(inputShape[1], this.poolSize[0], this.padding, this.strides[0]);
      return [inputShape[0], length, inputShape[2]];
    }
  }, {
    key: "call",
    value: function call(inputs, kwargs) {
      var _this2 = this;

      return tidy(function () {
        _this2.invokeCallHook(inputs, kwargs); // Add dummy last dimension.


        inputs = K.expandDims(getExactlyOneTensor(inputs), 2);

        var output = _this2.poolingFunction(getExactlyOneTensor(inputs), [_this2.poolSize[0], 1], [_this2.strides[0], 1], _this2.padding, 'channelsLast'); // Remove dummy last dimension.


        return tfc.squeeze(output, [2]);
      });
    }
  }, {
    key: "getConfig",
    value: function getConfig() {
      var config = {
        poolSize: this.poolSize,
        padding: this.padding,
        strides: this.strides
      };

      var baseConfig = _get(_getPrototypeOf(Pooling1D.prototype), "getConfig", this).call(this);

      Object.assign(config, baseConfig);
      return config;
    }
  }]);

  return Pooling1D;
}(Layer);
export var MaxPooling1D = /*#__PURE__*/function (_Pooling1D) {
  _inherits(MaxPooling1D, _Pooling1D);

  function MaxPooling1D(args) {
    _classCallCheck(this, MaxPooling1D);

    return _possibleConstructorReturn(this, _getPrototypeOf(MaxPooling1D).call(this, args));
  }

  _createClass(MaxPooling1D, [{
    key: "poolingFunction",
    value: function poolingFunction(inputs, poolSize, strides, padding, dataFormat) {
      checkDataFormat(dataFormat);
      checkPaddingMode(padding);
      return pool2d(inputs, poolSize, strides, padding, dataFormat, 'max');
    }
  }]);

  return MaxPooling1D;
}(Pooling1D);
/** @nocollapse */

MaxPooling1D.className = 'MaxPooling1D';
serialization.registerClass(MaxPooling1D);
export var AveragePooling1D = /*#__PURE__*/function (_Pooling1D2) {
  _inherits(AveragePooling1D, _Pooling1D2);

  function AveragePooling1D(args) {
    _classCallCheck(this, AveragePooling1D);

    return _possibleConstructorReturn(this, _getPrototypeOf(AveragePooling1D).call(this, args));
  }

  _createClass(AveragePooling1D, [{
    key: "poolingFunction",
    value: function poolingFunction(inputs, poolSize, strides, padding, dataFormat) {
      checkDataFormat(dataFormat);
      checkPaddingMode(padding);
      return pool2d(inputs, poolSize, strides, padding, dataFormat, 'avg');
    }
  }]);

  return AveragePooling1D;
}(Pooling1D);
/** @nocollapse */

AveragePooling1D.className = 'AveragePooling1D';
serialization.registerClass(AveragePooling1D);
/**
 * Abstract class for different pooling 2D layers.
 */

export var Pooling2D = /*#__PURE__*/function (_Layer2) {
  _inherits(Pooling2D, _Layer2);

  function Pooling2D(args) {
    var _this3;

    _classCallCheck(this, Pooling2D);

    if (args.poolSize == null) {
      args.poolSize = [2, 2];
    }

    _this3 = _possibleConstructorReturn(this, _getPrototypeOf(Pooling2D).call(this, args));
    _this3.poolSize = Array.isArray(args.poolSize) ? args.poolSize : [args.poolSize, args.poolSize];

    if (args.strides == null) {
      _this3.strides = _this3.poolSize;
    } else if (Array.isArray(args.strides)) {
      if (args.strides.length !== 2) {
        throw new ValueError("If the strides property of a 2D pooling layer is an Array, " + "it is expected to have a length of 2, but received length " + "".concat(args.strides.length, "."));
      }

      _this3.strides = args.strides;
    } else {
      // `config.strides` is a number.
      _this3.strides = [args.strides, args.strides];
    }

    assertPositiveInteger(_this3.poolSize, 'poolSize');
    assertPositiveInteger(_this3.strides, 'strides');
    _this3.padding = args.padding == null ? 'valid' : args.padding;
    _this3.dataFormat = args.dataFormat == null ? 'channelsLast' : args.dataFormat;
    checkDataFormat(_this3.dataFormat);
    checkPaddingMode(_this3.padding);
    _this3.inputSpec = [new InputSpec({
      ndim: 4
    })];
    return _this3;
  }

  _createClass(Pooling2D, [{
    key: "computeOutputShape",
    value: function computeOutputShape(inputShape) {
      inputShape = getExactlyOneShape(inputShape);
      var rows = this.dataFormat === 'channelsFirst' ? inputShape[2] : inputShape[1];
      var cols = this.dataFormat === 'channelsFirst' ? inputShape[3] : inputShape[2];
      rows = convOutputLength(rows, this.poolSize[0], this.padding, this.strides[0]);
      cols = convOutputLength(cols, this.poolSize[1], this.padding, this.strides[1]);

      if (this.dataFormat === 'channelsFirst') {
        return [inputShape[0], inputShape[1], rows, cols];
      } else {
        return [inputShape[0], rows, cols, inputShape[3]];
      }
    }
  }, {
    key: "call",
    value: function call(inputs, kwargs) {
      var _this4 = this;

      return tidy(function () {
        _this4.invokeCallHook(inputs, kwargs);

        return _this4.poolingFunction(getExactlyOneTensor(inputs), _this4.poolSize, _this4.strides, _this4.padding, _this4.dataFormat);
      });
    }
  }, {
    key: "getConfig",
    value: function getConfig() {
      var config = {
        poolSize: this.poolSize,
        padding: this.padding,
        strides: this.strides,
        dataFormat: this.dataFormat
      };

      var baseConfig = _get(_getPrototypeOf(Pooling2D.prototype), "getConfig", this).call(this);

      Object.assign(config, baseConfig);
      return config;
    }
  }]);

  return Pooling2D;
}(Layer);
export var MaxPooling2D = /*#__PURE__*/function (_Pooling2D) {
  _inherits(MaxPooling2D, _Pooling2D);

  function MaxPooling2D(args) {
    _classCallCheck(this, MaxPooling2D);

    return _possibleConstructorReturn(this, _getPrototypeOf(MaxPooling2D).call(this, args));
  }

  _createClass(MaxPooling2D, [{
    key: "poolingFunction",
    value: function poolingFunction(inputs, poolSize, strides, padding, dataFormat) {
      checkDataFormat(dataFormat);
      checkPaddingMode(padding);
      return pool2d(inputs, poolSize, strides, padding, dataFormat, 'max');
    }
  }]);

  return MaxPooling2D;
}(Pooling2D);
/** @nocollapse */

MaxPooling2D.className = 'MaxPooling2D';
serialization.registerClass(MaxPooling2D);
export var AveragePooling2D = /*#__PURE__*/function (_Pooling2D2) {
  _inherits(AveragePooling2D, _Pooling2D2);

  function AveragePooling2D(args) {
    _classCallCheck(this, AveragePooling2D);

    return _possibleConstructorReturn(this, _getPrototypeOf(AveragePooling2D).call(this, args));
  }

  _createClass(AveragePooling2D, [{
    key: "poolingFunction",
    value: function poolingFunction(inputs, poolSize, strides, padding, dataFormat) {
      checkDataFormat(dataFormat);
      checkPaddingMode(padding);
      return pool2d(inputs, poolSize, strides, padding, dataFormat, 'avg');
    }
  }]);

  return AveragePooling2D;
}(Pooling2D);
/** @nocollapse */

AveragePooling2D.className = 'AveragePooling2D';
serialization.registerClass(AveragePooling2D);
/**
 * Abstract class for different pooling 3D layers.
 */

export var Pooling3D = /*#__PURE__*/function (_Layer3) {
  _inherits(Pooling3D, _Layer3);

  function Pooling3D(args) {
    var _this5;

    _classCallCheck(this, Pooling3D);

    if (args.poolSize == null) {
      args.poolSize = [2, 2, 2];
    }

    _this5 = _possibleConstructorReturn(this, _getPrototypeOf(Pooling3D).call(this, args));
    _this5.poolSize = Array.isArray(args.poolSize) ? args.poolSize : [args.poolSize, args.poolSize, args.poolSize];

    if (args.strides == null) {
      _this5.strides = _this5.poolSize;
    } else if (Array.isArray(args.strides)) {
      if (args.strides.length !== 3) {
        throw new ValueError("If the strides property of a 3D pooling layer is an Array, " + "it is expected to have a length of 3, but received length " + "".concat(args.strides.length, "."));
      }

      _this5.strides = args.strides;
    } else {
      // `config.strides` is a number.
      _this5.strides = [args.strides, args.strides, args.strides];
    }

    assertPositiveInteger(_this5.poolSize, 'poolSize');
    assertPositiveInteger(_this5.strides, 'strides');
    _this5.padding = args.padding == null ? 'valid' : args.padding;
    _this5.dataFormat = args.dataFormat == null ? 'channelsLast' : args.dataFormat;
    checkDataFormat(_this5.dataFormat);
    checkPaddingMode(_this5.padding);
    _this5.inputSpec = [new InputSpec({
      ndim: 5
    })];
    return _this5;
  }

  _createClass(Pooling3D, [{
    key: "computeOutputShape",
    value: function computeOutputShape(inputShape) {
      inputShape = getExactlyOneShape(inputShape);
      var depths = this.dataFormat === 'channelsFirst' ? inputShape[2] : inputShape[1];
      var rows = this.dataFormat === 'channelsFirst' ? inputShape[3] : inputShape[2];
      var cols = this.dataFormat === 'channelsFirst' ? inputShape[4] : inputShape[3];
      depths = convOutputLength(depths, this.poolSize[0], this.padding, this.strides[0]);
      rows = convOutputLength(rows, this.poolSize[1], this.padding, this.strides[1]);
      cols = convOutputLength(cols, this.poolSize[2], this.padding, this.strides[2]);

      if (this.dataFormat === 'channelsFirst') {
        return [inputShape[0], inputShape[1], depths, rows, cols];
      } else {
        return [inputShape[0], depths, rows, cols, inputShape[4]];
      }
    }
  }, {
    key: "call",
    value: function call(inputs, kwargs) {
      var _this6 = this;

      return tidy(function () {
        _this6.invokeCallHook(inputs, kwargs);

        return _this6.poolingFunction(getExactlyOneTensor(inputs), _this6.poolSize, _this6.strides, _this6.padding, _this6.dataFormat);
      });
    }
  }, {
    key: "getConfig",
    value: function getConfig() {
      var config = {
        poolSize: this.poolSize,
        padding: this.padding,
        strides: this.strides,
        dataFormat: this.dataFormat
      };

      var baseConfig = _get(_getPrototypeOf(Pooling3D.prototype), "getConfig", this).call(this);

      Object.assign(config, baseConfig);
      return config;
    }
  }]);

  return Pooling3D;
}(Layer);
export var MaxPooling3D = /*#__PURE__*/function (_Pooling3D) {
  _inherits(MaxPooling3D, _Pooling3D);

  function MaxPooling3D(args) {
    _classCallCheck(this, MaxPooling3D);

    return _possibleConstructorReturn(this, _getPrototypeOf(MaxPooling3D).call(this, args));
  }

  _createClass(MaxPooling3D, [{
    key: "poolingFunction",
    value: function poolingFunction(inputs, poolSize, strides, padding, dataFormat) {
      checkDataFormat(dataFormat);
      checkPaddingMode(padding);
      return pool3d(inputs, poolSize, strides, padding, dataFormat, 'max');
    }
  }]);

  return MaxPooling3D;
}(Pooling3D);
/** @nocollapse */

MaxPooling3D.className = 'MaxPooling3D';
serialization.registerClass(MaxPooling3D);
export var AveragePooling3D = /*#__PURE__*/function (_Pooling3D2) {
  _inherits(AveragePooling3D, _Pooling3D2);

  function AveragePooling3D(args) {
    _classCallCheck(this, AveragePooling3D);

    return _possibleConstructorReturn(this, _getPrototypeOf(AveragePooling3D).call(this, args));
  }

  _createClass(AveragePooling3D, [{
    key: "poolingFunction",
    value: function poolingFunction(inputs, poolSize, strides, padding, dataFormat) {
      checkDataFormat(dataFormat);
      checkPaddingMode(padding);
      return pool3d(inputs, poolSize, strides, padding, dataFormat, 'avg');
    }
  }]);

  return AveragePooling3D;
}(Pooling3D);
/** @nocollapse */

AveragePooling3D.className = 'AveragePooling3D';
serialization.registerClass(AveragePooling3D);
/**
 * Abstract class for different global pooling 1D layers.
 */

export var GlobalPooling1D = /*#__PURE__*/function (_Layer4) {
  _inherits(GlobalPooling1D, _Layer4);

  function GlobalPooling1D(args) {
    var _this7;

    _classCallCheck(this, GlobalPooling1D);

    _this7 = _possibleConstructorReturn(this, _getPrototypeOf(GlobalPooling1D).call(this, args));
    _this7.inputSpec = [new InputSpec({
      ndim: 3
    })];
    return _this7;
  }

  _createClass(GlobalPooling1D, [{
    key: "computeOutputShape",
    value: function computeOutputShape(inputShape) {
      return [inputShape[0], inputShape[2]];
    }
  }, {
    key: "call",
    value: function call(inputs, kwargs) {
      throw new NotImplementedError();
    }
  }]);

  return GlobalPooling1D;
}(Layer);
export var GlobalAveragePooling1D = /*#__PURE__*/function (_GlobalPooling1D) {
  _inherits(GlobalAveragePooling1D, _GlobalPooling1D);

  function GlobalAveragePooling1D(args) {
    _classCallCheck(this, GlobalAveragePooling1D);

    return _possibleConstructorReturn(this, _getPrototypeOf(GlobalAveragePooling1D).call(this, args || {}));
  }

  _createClass(GlobalAveragePooling1D, [{
    key: "call",
    value: function call(inputs, kwargs) {
      return tidy(function () {
        var input = getExactlyOneTensor(inputs);
        return tfc.mean(input, 1);
      });
    }
  }]);

  return GlobalAveragePooling1D;
}(GlobalPooling1D);
/** @nocollapse */

GlobalAveragePooling1D.className = 'GlobalAveragePooling1D';
serialization.registerClass(GlobalAveragePooling1D);
export var GlobalMaxPooling1D = /*#__PURE__*/function (_GlobalPooling1D2) {
  _inherits(GlobalMaxPooling1D, _GlobalPooling1D2);

  function GlobalMaxPooling1D(args) {
    _classCallCheck(this, GlobalMaxPooling1D);

    return _possibleConstructorReturn(this, _getPrototypeOf(GlobalMaxPooling1D).call(this, args || {}));
  }

  _createClass(GlobalMaxPooling1D, [{
    key: "call",
    value: function call(inputs, kwargs) {
      return tidy(function () {
        var input = getExactlyOneTensor(inputs);
        return tfc.max(input, 1);
      });
    }
  }]);

  return GlobalMaxPooling1D;
}(GlobalPooling1D);
/** @nocollapse */

GlobalMaxPooling1D.className = 'GlobalMaxPooling1D';
serialization.registerClass(GlobalMaxPooling1D);
/**
 * Abstract class for different global pooling 2D layers.
 */

export var GlobalPooling2D = /*#__PURE__*/function (_Layer5) {
  _inherits(GlobalPooling2D, _Layer5);

  function GlobalPooling2D(args) {
    var _this8;

    _classCallCheck(this, GlobalPooling2D);

    _this8 = _possibleConstructorReturn(this, _getPrototypeOf(GlobalPooling2D).call(this, args));
    _this8.dataFormat = args.dataFormat == null ? 'channelsLast' : args.dataFormat;
    checkDataFormat(_this8.dataFormat);
    _this8.inputSpec = [new InputSpec({
      ndim: 4
    })];
    return _this8;
  }

  _createClass(GlobalPooling2D, [{
    key: "computeOutputShape",
    value: function computeOutputShape(inputShape) {
      inputShape = inputShape;

      if (this.dataFormat === 'channelsLast') {
        return [inputShape[0], inputShape[3]];
      } else {
        return [inputShape[0], inputShape[1]];
      }
    }
  }, {
    key: "call",
    value: function call(inputs, kwargs) {
      throw new NotImplementedError();
    }
  }, {
    key: "getConfig",
    value: function getConfig() {
      var config = {
        dataFormat: this.dataFormat
      };

      var baseConfig = _get(_getPrototypeOf(GlobalPooling2D.prototype), "getConfig", this).call(this);

      Object.assign(config, baseConfig);
      return config;
    }
  }]);

  return GlobalPooling2D;
}(Layer);
export var GlobalAveragePooling2D = /*#__PURE__*/function (_GlobalPooling2D) {
  _inherits(GlobalAveragePooling2D, _GlobalPooling2D);

  function GlobalAveragePooling2D() {
    _classCallCheck(this, GlobalAveragePooling2D);

    return _possibleConstructorReturn(this, _getPrototypeOf(GlobalAveragePooling2D).apply(this, arguments));
  }

  _createClass(GlobalAveragePooling2D, [{
    key: "call",
    value: function call(inputs, kwargs) {
      var _this9 = this;

      return tidy(function () {
        var input = getExactlyOneTensor(inputs);

        if (_this9.dataFormat === 'channelsLast') {
          return tfc.mean(input, [1, 2]);
        } else {
          return tfc.mean(input, [2, 3]);
        }
      });
    }
  }]);

  return GlobalAveragePooling2D;
}(GlobalPooling2D);
/** @nocollapse */

GlobalAveragePooling2D.className = 'GlobalAveragePooling2D';
serialization.registerClass(GlobalAveragePooling2D);
export var GlobalMaxPooling2D = /*#__PURE__*/function (_GlobalPooling2D2) {
  _inherits(GlobalMaxPooling2D, _GlobalPooling2D2);

  function GlobalMaxPooling2D() {
    _classCallCheck(this, GlobalMaxPooling2D);

    return _possibleConstructorReturn(this, _getPrototypeOf(GlobalMaxPooling2D).apply(this, arguments));
  }

  _createClass(GlobalMaxPooling2D, [{
    key: "call",
    value: function call(inputs, kwargs) {
      var _this10 = this;

      return tidy(function () {
        var input = getExactlyOneTensor(inputs);

        if (_this10.dataFormat === 'channelsLast') {
          return tfc.max(input, [1, 2]);
        } else {
          return tfc.max(input, [2, 3]);
        }
      });
    }
  }]);

  return GlobalMaxPooling2D;
}(GlobalPooling2D);
/** @nocollapse */

GlobalMaxPooling2D.className = 'GlobalMaxPooling2D';
serialization.registerClass(GlobalMaxPooling2D);